/* Email-inbound secrets pill for the Admin Hub.
 *
 * Polls /api/admin/email-inbound/diagnostics on mount and every 5 min.
 * Shows a tiny green chip when the three canonical Cloudflare Worker
 * secrets are all set on the pod:
 *   • WEBHOOK_SECRET       (Worker → API, HMAC verify inbound)
 *   • APP_OUTBOUND_SECRET  (API → Worker, X-App-Secret on outbound /send)
 *   • APP_INBOUND_URL      (Worker → API, POST target)
 *
 * Flashes amber the second any of them drop off, with a deep-link to
 * the Deployment Secrets UI + a re-check-now button. Silent otherwise
 * so a healthy Admin Hub stays uncluttered.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, ShieldCheck, Loader2 } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

function ageLabel(iso) {
  if (!iso) return 'never';
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms)) return 'never';
  const m = Math.round(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

// The three secrets we care about. Anything else on the diagnostics
// checklist (Cloudflare dashboard rules, apex catch-all) is
// operator-side and can't be probed from here.
const SECRET_STEPS = ['WEBHOOK_SECRET', 'APP_OUTBOUND_SECRET', 'APP_INBOUND_URL'];

function summarize(checklist) {
  const rows = (checklist || []).filter(
    (c) => SECRET_STEPS.some((k) => c.step && c.step.includes(k)),
  );
  const missing = rows.filter((c) => c.ok === false);
  return { rows, missing };
}

export default function EmailInboundSecretsPill() {
  const [state, setState] = useState({ loading: true });

  const check = async () => {
    try {
      const r = await http.get('/admin/email-inbound/diagnostics');
      setState({ loading: false, at: new Date().toISOString(), ...r.data });
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || 'Could not read diagnostics.', at: null });
    }
  };

  useEffect(() => {
    check();
    const t = window.setInterval(check, 5 * 60_000);
    return () => window.clearInterval(t);
  }, []);

  if (state.loading) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-0.5 text-[10.5px] text-muted-foreground"
           data-testid="email-inbound-pill-loading">
        <Loader2 className="h-3 w-3 animate-spin" />
        Email secrets…
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/50 bg-amber-500/10 px-2 py-0.5 text-[10.5px] text-amber-700 dark:text-amber-300"
           data-testid="email-inbound-pill-error"
           title={state.error}>
        <AlertTriangle className="h-3 w-3" />
        Email secrets · unreachable
      </div>
    );
  }

  const { rows, missing } = summarize(state.checklist);
  if (rows.length === 0) return null;

  // Healthy: tiny green chip.
  if (missing.length === 0) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10.5px] text-emerald-700 dark:text-emerald-400"
           data-testid="email-inbound-pill-ok"
           title={`Last check: ${ageLabel(state.at)} — WEBHOOK_SECRET · APP_OUTBOUND_SECRET · APP_INBOUND_URL all set`}>
        <ShieldCheck className="h-3 w-3" />
        Email inbound · armed
      </div>
    );
  }

  // Degraded: amber pill with fix instructions.
  const missingNames = missing
    .map((c) => SECRET_STEPS.find((k) => c.step && c.step.includes(k)))
    .filter(Boolean);
  return (
    <div className="rounded-md border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-900 dark:text-amber-200"
         data-testid="email-inbound-pill-warn">
      <div className="flex items-center gap-2 font-semibold">
        <AlertTriangle className="h-3.5 w-3.5" />
        Email inbound — {missing.length === 1 ? 'secret missing' : `${missing.length} secrets missing`}
        <span className="ml-1 rounded-full bg-amber-500/20 px-1.5 py-0 text-[10px] font-normal uppercase tracking-wider">
          {ageLabel(state.at)}
        </span>
      </div>
      <div className="mt-0.5 pl-5 text-[11px] opacity-90">
        Not set: {missingNames.map((n) => <code key={n} className="mx-0.5 rounded bg-amber-500/20 px-1 text-[10.5px]">{n}</code>)}
        {' '}— the Cloudflare Worker cannot deliver inbound mail until these are configured.
        {'  '}
        <a href="https://app.emergent.sh/"
           target="_blank" rel="noopener noreferrer"
           data-testid="email-inbound-pill-fix-link"
           className="font-semibold underline decoration-amber-400 hover:decoration-amber-600">
          Open Deployment → Secrets →
        </a>
        {'  '}
        <button type="button" onClick={check}
                data-testid="email-inbound-pill-recheck"
                className="underline decoration-dotted underline-offset-2 hover:decoration-solid">
          re-check now
        </button>
      </div>
    </div>
  );
}
